import { NonFieldError } from "@/components/common/SCAlert";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import {
  NOT_SELECTED,
  hasPastRemindItem,
  updateHour,
  updateMinutes,
  validate,
} from "@/components/examinee/daily_interview_remind/RemindService";
import {
  RemindItem,
  SimpleTime,
} from "@/components/examinee/daily_interview_remind/Types";
import { useMyPageAPI } from "@/components/examinee/mypage/MyPageAPI";
import { RemindTimeListForm } from "@/components/examinee/mypage/RemindTimeListForm";
import { GeneralError } from "@/components/sleep_checkup_v1/GeneralError";
import constants from "@/utils/constants";
import { Button, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addDays, format, isValid, startOfDay, subDays } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/**
 * 測定期間の登録ページ（要ログイン）。
 * 測定開始日と各日のリマインド受信時刻を登録すると、測定期間（8 日間）が確定し
 * 毎日問診のリマインドが自動生成される（#1778: 受信時刻は 1 日ごとに設定）。
 * 開始日は前日（昨夜の睡眠）まで選択可。前日を選んだ場合の 1 日目（= 本日）の受信時刻は
 * 現在時刻より後のみ選択・登録できる（#1821: 5 分毎の送信バッチによる即時送信を防ぐ）。
 */
export const MeasurementPeriodRegistration = () => {
  const { sleepCheckupUuid } = useParams();
  const navigate = useNavigate();
  const api = useMyPageAPI();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [remindItems, setRemindItems] = useState<RemindItem[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 開始日は前日（昨夜の睡眠）まで選択可（#1821）。2 日前以前は不可
  const now = new Date();
  const minStartDate = startOfDay(subDays(now, 1));
  const isStartDateValid =
    startDate != null && isValid(startDate) && minStartDate <= startDate;

  // 開始日確定時に 8 日分の日付を生成する。
  // 毎日問診は前夜の睡眠について起床後に回答するため、リマインドは開始日の翌日から
  // 8 日分（backend の start_date + (i + 1) 日と一致させる。#1793）。
  // 開始日を変更しても選択済みの時刻は保持し、日付のみ更新する（plan.md Step 2）。
  useEffect(() => {
    if (!isStartDateValid) {
      setRemindItems([]);
      return;
    }
    const base = startOfDay(startDate);
    setRemindItems((prev) =>
      Array.from({ length: constants.SLEEP_CHECKUP_LENGTH }, (_, i) => {
        const time =
          prev[i]?.time ?? new SimpleTime(NOT_SELECTED, NOT_SELECTED);
        return new RemindItem(addDays(base, i + 1), time);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartDateValid, startDate?.getTime()]);

  if (!sleepCheckupUuid) {
    return <GeneralError to="/examinee/mypage" buttonText="マイページへ" />;
  }

  const endDate = isStartDateValid
    ? addDays(startDate, constants.SLEEP_CHECKUP_LENGTH - 1)
    : null;
  // 前日開始時の 1 日目（本日）に現在時刻以前の時刻が選ばれていたら登録不可（#1821）
  const hasPastTime = hasPastRemindItem(remindItems, now);
  const isRemindItemsValid =
    remindItems.length === constants.SLEEP_CHECKUP_LENGTH &&
    validate(remindItems) &&
    !hasPastTime;

  const handleClickRegister = () => {
    if (!isStartDateValid || !isRemindItemsValid || submitting) {
      return;
    }
    // 画面表示後に時間が経過し、選択済みの時刻が過去になったケースを押下時に再判定する（#1821）
    if (hasPastRemindItem(remindItems, new Date())) {
      setErrorMessage(
        "1 日目の受信時刻が現在時刻を過ぎています。現在時刻より後の時刻を選択してください。"
      );
      window.scrollTo(0, 0);
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    // 各日の時刻を日付順に "HH:MM" で 8 件生成する
    const remindTimes = remindItems.map(
      (item) => `${item.time.hour}:${item.time.minutes}`
    );
    api
      .registerMeasurementPeriod(
        sleepCheckupUuid,
        format(startDate, "yyyy-MM-dd"),
        remindTimes
      )
      .then(() => {
        navigate("/examinee/mypage");
      })
      .catch((err) => {
        setSubmitting(false);
        const responseData = err.response?.data;
        const messages: string[] = [
          ...(responseData?.error_messages ?? []),
          ...(responseData?.start_date ?? []),
          ...(responseData?.remind_times ?? []),
        ];
        if (messages.length > 0) {
          setErrorMessage(messages[0]);
        } else {
          setErrorMessage(
            "エラーが発生しました。申し訳ありませんが、少し時間をおいてから再度お試しください。"
          );
        }
        window.scrollTo(0, 0);
      });
  };

  return (
    <Stack spacing={8} sx={{ mt: 8, mb: 16, mx: 4 }}>
      <ScrollToTop />
      <Stack spacing={6}>
        <Typography variant="h6" align="center">
          測定期間の登録
        </Typography>

        {/* エラーメッセージ */}
        <NonFieldError>{errorMessage}</NonFieldError>

        <Typography
          variant="subtitle2"
          textAlign="justify"
          sx={{ color: "text.secondary" }}
        >
          測定を開始する日を選択してください。測定期間は開始日を含む
          {constants.SLEEP_CHECKUP_LENGTH}
          日間です。期間中は毎日、睡眠に関する問診にご回答いただきます。
          昨夜の睡眠から測定に含めたい場合は、前日を開始日として選択できます。その場合、1
          日目の受信時刻は現在時刻より後の時刻を選択してください。
        </Typography>

        <Stack spacing={1}>
          <Typography variant="subtitle2">測定開始日</Typography>
          <DatePicker
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            minDate={minStartDate}
            format="yyyy/MM/dd"
            // モバイルダイアログのツールバー（英語タイトル「SELECT DATE」と選択日付表示）を非表示にする
            slotProps={{ toolbar: { hidden: true } }}
          />
        </Stack>

        {startDate != null && endDate != null && (
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            測定期間：{format(startDate, "yyyy/MM/dd")} 〜{" "}
            {format(endDate, "yyyy/MM/dd")}
          </Typography>
        )}

        {isStartDateValid && remindItems.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              毎日問診リマインドの受信時刻
            </Typography>
            <Typography variant="body2" sx={{ px: 3, color: "text.secondary" }}>
              ※測定は開始日の就寝時から始まり、翌日の起床後から毎日問診にご回答いただきます。問診の通知メールをお送りするため、おおよその起床時刻を設定してください
            </Typography>
            <RemindTimeListForm
              remindItems={remindItems}
              now={now}
              onChangeHour={(index, hour) =>
                setRemindItems(updateHour(remindItems, index, hour))
              }
              onChangeMinutes={(index, minutes) =>
                setRemindItems(updateMinutes(remindItems, index, minutes))
              }
            />
          </Stack>
        )}
      </Stack>

      <Button
        variant="contained"
        size="large"
        onClick={handleClickRegister}
        disabled={!isStartDateValid || !isRemindItemsValid || submitting}
      >
        登録する
      </Button>
    </Stack>
  );
};
